import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, XCircle, Bell, BellOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else if (user) {
      fetchSubscriptionStatus();
    }
  }, [user, authLoading, navigate]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <Navbar />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <p className="text-xl text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <h1 
          data-testid="dashboard-heading"
          className="text-4xl sm:text-5xl font-bold mb-8"
          style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}
        >
          Dashboard
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Account Information */}
          <div data-testid="account-info" className="p-6 bg-slate-900 rounded-sm border border-slate-800">
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
              ACCOUNT INFO
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-slate-400 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>Name</p>
                <p className="text-white font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>{user?.full_name}</p>
              </div>
              <div>
                <p className="text-slate-400 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>Email</p>
                <p className="text-white font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>{user?.email}</p>
              </div>
              <div>
                <p className="text-slate-400 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>Member Since</p>
                <p className="text-white font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>{formatDate(user?.created_at)}</p>
              </div>
            </div>
          </div>

          {/* Subscription Status */}
          <div data-testid="subscription-status" className="p-6 bg-slate-900 rounded-sm border border-slate-800">
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
              SUBSCRIPTION
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-slate-400 text-sm mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>Status</p>
                {subscriptionStatus?.status === 'active' ? (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-green-500 font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>ACTIVE</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-slate-500" />
                    <span className="text-slate-500 font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>FREE</span>
                  </div>
                )}
              </div>
              
              {subscriptionStatus?.status === 'active' ? (
                <>
                  <div>
                    <p className="text-slate-400 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>Plan</p>
                    <p className="text-white font-medium capitalize" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {subscriptionStatus?.plan || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>Renews On</p>
                    <p className="text-white font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {formatDate(subscriptionStatus?.end_date)}
                    </p>
                  </div>
                </>
              ) : (
                <div className="mt-4">
                  <Link to="/pricing">
                    <Button className="w-full bg-red-600 hover:bg-red-700 text-white rounded-sm font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>
                      Upgrade to Premium
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Email Notifications */}
          <div data-testid="notification-settings" className="p-6 bg-slate-900 rounded-sm border border-slate-800">
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
              NOTIFICATIONS
            </h2>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {subscriptionStatus?.email_notifications ? (
                  <Bell className="w-5 h-5 text-green-500" />
                ) : (
                  <BellOff className="w-5 h-5 text-slate-500" />
                )}
                <div>
                  <p className="text-white font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>Email Notifications</p>
                  <p className="text-slate-400 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {subscriptionStatus?.email_notifications ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div data-testid="quick-actions" className="p-6 bg-slate-900 rounded-sm border border-slate-800">
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
              QUICK ACTIONS
            </h2>
            <div className="space-y-3">
              <Link to="/videos" className="block">
                <Button variant="outline" className="w-full border-slate-700 text-white hover:bg-slate-800 rounded-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Browse Videos
                </Button>
              </Link>
              {subscriptionStatus?.status !== 'active' && (
                <Link to="/pricing" className="block">
                  <Button className="w-full bg-red-600 hover:bg-red-700 text-white rounded-sm font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    View Pricing
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
